/**
 * Componente para mostrar cada imagen en el grid.
 * Adaptado para la web con soporte de selección y clic.
 */

import React, { useState } from 'react';
import { FiCheck, FiTrash2 } from 'react-icons/fi';

/**
 * Componente ImageGridItem
 * 
 * Muestra una imagen individual en el grid con soporte para selección
 * a través de click derecho (botón derecho del mouse) y apertura de carrusel
 * a través de click normal (botón izquierdo).
 * 
 * INTERACCIÓN:
 * - Click normal (botón izquierdo): Abre el carrusel de imágenes
 * - Click derecho (botón derecho): Selecciona la imagen para eliminación
 * - Modo edición: Muestra badge de eliminación en esquina superior izquierda
 * 
 * @param {Object} foto - Objeto de imagen con img_id y url
 * @param {boolean} selected - Indica si la imagen está seleccionada
 * @param {number} sizeW - Ancho del componente
 * @param {number} sizeH - Altura del componente
 * @param {Function} onLongPress - Callback al hacer click derecho (para selección)
 * @param {Function} onPress - Callback al hacer click normal (para abrir carrusel)
 * @param {boolean} isEditMode - Indica si está en modo de edición (shake mode)
 * @param {Function} onDeleteSingleImage - Callback al hacer click en badge de eliminación
 */
export default function ImageGridItem({ 
  foto, 
  selected, 
  sizeW, 
  sizeH, 
  onLongPress, 
  onPress,
  isEditMode = false,
  onDeleteSingleImage
}) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(true);
  };

  const handleDeleteBadgeClick = (e) => {
    e.stopPropagation(); // Prevenir que se abra el carrusel
    if (onDeleteSingleImage) {
      onDeleteSingleImage(foto);
    }
  };

  return (
    <div className='py-1.5 w-full'>
      <div
        onContextMenu={(e) => {
          e.preventDefault();
          onLongPress?.(foto);
        }}
        onClick={() => onPress?.(foto)}
        style={{ 
          position: 'relative',
          cursor: 'pointer',
          width: '100%',
          aspectRatio: '1 / 1'
        }}
        className={`relative ${isEditMode ? 'shake-animation' : ''}`}
      >
        {foto?.img_id && foto?.url && !imageError ? (
          <>
            {!imageLoaded && (
              <div 
              className="bg-neutral-900"
              style={{ 
                width: '100%', 
                height: '100%',
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                position: 'absolute',
                top: 0,
                left: 0
              }}>
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600"></div>
              </div>
            )}
            <img
              src={foto.url}
              alt="Imagen"
              style={{ width: '100%', height: '100%' }}
              className='rounded-lg object-cover'
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
          </>
        ) : (
          <div className="bg-neutral-900" style={{ 
            width: '100%', 
            height: '100%',
            backgroundColor: 'plum', 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center' 
          }}>
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600"></div>
          </div>
        )}
        {selected && (
          <div
            className='rounded-lg absolute top-0 left-0 flex items-center justify-center pointer-events-none'
            style={{
              width: '100%',
              height: '100%',
              borderWidth: '4px',
              borderColor: '#DA0037',
              backgroundColor: 'rgba(218, 0, 55, 0.3)'
            }}
          >
            <FiCheck
              size={32}
              color='white'
            />
          </div>
        )}
        {/* Badge de eliminación en modo edición (shake mode) */}
        {isEditMode && (
          <button
            onClick={handleDeleteBadgeClick}
            className='absolute top-2 left-2 bg-red-600 hover:bg-red-700 text-white rounded-full p-2 shadow-lg transition-colors z-10'
            title='Eliminar imagen'
            style={{
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <FiTrash2 size={16} />
          </button>
        )}
      </div>
    </div>
  );
}
